import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './MijnOutputs.module.css';

const OUTPUT_LABELS = {
  sbar:              'SBAR',
  artsconsult:       'Artsconsult',
  rapportage:        'Verpleegkundige Rapportage',
  dienst_overdracht: 'Dienst-overdracht',
  familie:           'Familiecommunicatie',
  overplaatsing:     'Overplaatsingsoverdracht',
};

const OUTPUT_KLEUREN = {
  sbar:              { bg: '#EEEEFF', kleur: '#2D2B6B' },
  artsconsult:       { bg: '#E8F4FD', kleur: '#1a6fa8' },
  rapportage:        { bg: '#F0FBF4', kleur: '#1A6B3C' },
  dienst_overdracht: { bg: '#FFF8E6', kleur: '#B85C00' },
  familie:           { bg: '#FDF0FF', kleur: '#7B2D8B' },
  overplaatsing:     { bg: '#FFF0F0', kleur: '#CC0000' },
};

const URGENTIE_KLEUREN = {
  acuut:            { bg:'#FFF0F0', kleur:'#CC0000', label:'Acuut' },
  spoed:            { bg:'#FFF8E6', kleur:'#B85C00', label:'Spoed' },
  niet_spoedeisend: { bg:'#F0FBF4', kleur:'#1A6B3C', label:'Niet spoedeisend' },
};

const SBAR_CONFIG = {
  S: { rand:'#2D2B6B', label:'Situation',      sub:'Huidige situatie' },
  B: { rand:'#3B5FC0', label:'Background',     sub:'Achtergrond' },
  A: { rand:'#B85C00', label:'Assessment',     sub:'Beoordeling' },
  R: { rand:'#1A6B3C', label:'Recommendation', sub:'Aanbeveling' },
};

const EXTRA_TYPES = [
  { c:'sbar',              l:'SBAR',                      p:'outputs.sbar' },
  { c:'artsconsult',       l:'Artsconsult',               p:'outputs.artsconsult' },
  { c:'rapportage',        l:'Rapportage',                p:'outputs.rapportage' },
  { c:'dienst_overdracht', l:'Dienst-overdracht',         p:'outputs.dienst_overdracht' },
  { c:'familie',           l:'Familiecommunicatie',       p:'outputs.familie' },
  { c:'overplaatsing',     l:'Overplaatsingsoverdracht',  p:'outputs.overplaatsing' },
];

export default function MijnOutputs() {
  const { heeftPermissie, heeftModule } = useAuth();
  const [sessies, setSessies]               = useState([]);
  const [laden, setLaden]                   = useState(true);
  const [openSessieId, setOpenSessieId]     = useState(null);
  const [geselecteerde, setGeselecteerde]   = useState(null);
  const [detailLaden, setDetailLaden]       = useState(false);
  const [verwijderModal, setVerwijderModal] = useState(null);
  const [extraLaden, setExtraLaden]         = useState(false);
  const [extraType, setExtraType]           = useState('');
  const [toonExtraKeuze, setToonExtraKeuze] = useState(false);
  const [mobieleDetailOpen, setMobieleDetailOpen] = useState(false);
  const [zoekterm, setZoekterm]             = useState('');

  const laadSessies = useCallback(async () => {
    try {
      const res = await axios.get('/api/outputs/mijn');
      setSessies(res.data.sessies || []);
    } catch {
      toast.error('Sessies ophalen mislukt.');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { laadSessies(); }, [laadSessies]);

  async function openOutput(output) {
    setDetailLaden(true);
    setGeselecteerde(null);
    setMobieleDetailOpen(true);
    try {
      const res = await axios.get(`/api/outputs/${output.id}`);
      setGeselecteerde(res.data.output);
    } catch {
      toast.error('Output laden mislukt.');
    } finally {
      setDetailLaden(false);
    }
  }

  async function handleVerwijderSessie(sessieId) {
    try {
      await axios.delete(`/api/outputs/sessies/${sessieId}`);
      toast.success('Sessie verwijderd.');
      setVerwijderModal(null);
      setGeselecteerde(null);
      setOpenSessieId(null);
      laadSessies();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verwijderen mislukt.');
    }
  }

  async function handleExtraOutput(sessieId) {
    if (!extraType) return toast.error('Kies een outputtype.');
    setExtraLaden(true);
    try {
      const res = await axios.post('/api/outputs/extra', {
        sessie_id:   sessieId,
        output_type: extraType,
      });
      toast.success(`${OUTPUT_LABELS[extraType]} gegenereerd.`);
      setToonExtraKeuze(false);
      setExtraType('');
      await laadSessies();
      // Open de nieuwe output via het normale pad zodat id correct is
      if (res.data.output_id) {
        const detail = await axios.get(`/api/outputs/${res.data.output_id}`);
        setGeselecteerde(detail.data.output);
        setMobieleDetailOpen(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Genereren mislukt.');
    } finally {
      setExtraLaden(false);
    }
  }

  async function handlePdf(outputId) {
    try {
      const res = await axios.get(`/api/outputs/${outputId}/pdf`, { responseType: 'blob' });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = `alyden-output-${outputId}.pdf`; link.click();
    } catch { toast.error('PDF genereren mislukt.'); }
  }

  function parseInhoud(inhoud, outputType) {
    if (outputType === 'sbar') {
      try {
        const data = typeof inhoud === 'object'
          ? inhoud
          : JSON.parse(inhoud.replace(/```json\n?|\n?```/g, '').trim());
        return data;
      } catch { return null; }
    }
    return null;
  }

  function renderMarkdown(tekst) {
    if (!tekst) return null;
    const str = typeof tekst === 'string' ? tekst : JSON.stringify(tekst, null, 2);
    const cleaned = str.replace(/```[\w]*\n?|\n?```/g, '').trim();
    const lines = cleaned.split('\n');
    const elements = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) { elements.push(<br key={i} />); i++; continue; }
      if (line.trim().match(/^\*\*.*\*\*$/)) {
        elements.push(<h3 key={i} className={styles.mdKop}>{line.trim().replace(/\*\*/g,'')}</h3>);
      } else if (line.trim().match(/^[-*]\s/)) {
        const item = line.trim().replace(/^[-*]\s/, '');
        const parts = item.split(/\*\*(.*?)\*\*/g);
        elements.push(<div key={i} className={styles.mdLijstItem}><span className={styles.mdBullet}>•</span><span>{parts.map((p,j)=>j%2===1?<strong key={j}>{p}</strong>:p)}</span></div>);
      } else if (line.trim().match(/^\d+\.\s/)) {
        const match = line.trim().match(/^(\d+)\.\s(.*)$/);
        elements.push(<div key={i} className={styles.mdLijstItem}><span className={styles.mdNummer}>{match[1]}.</span><span>{match[2]}</span></div>);
      } else {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        elements.push(<p key={i} className={styles.mdParagraaf}>{parts.map((p,j)=>j%2===1?<strong key={j}>{p}</strong>:p)}</p>);
      }
      i++;
    }
    return elements;
  }

  const gefilterdeSeSSies = sessies.filter(s => {
    if (!zoekterm) return true;
    const z = zoekterm.toLowerCase();
    return (s.omschrijving||'').toLowerCase().includes(z) ||
           (s.urgentie||'').toLowerCase().includes(z) ||
           (s.gebruiker_naam||'').toLowerCase().includes(z);
  });

  return (
    <div className={styles.pagina}>
      <div className={styles.paginaHeader}>
        <h1 className={styles.titel}>Mijn outputs</h1>
        <input type="text" className={styles.zoek}
          placeholder="Zoek op omschrijving of urgentie..."
          value={zoekterm} onChange={e => setZoekterm(e.target.value)} />
      </div>

      {mobieleDetailOpen && (
        <button className={styles.mobieleTerug}
          onClick={() => { setMobieleDetailOpen(false); setGeselecteerde(null); }}>
          ← Terug naar overzicht
        </button>
      )}

      <div className={`${styles.layout} ${mobieleDetailOpen ? styles.detailActief : ''}`}>

        {/* ── Sessie lijst ── */}
        <div className={styles.lijst}>
          {laden ? (
            <div className={styles.laadTekst}>Laden...</div>
          ) : gefilterdeSeSSies.length === 0 ? (
            <div className={styles.leeg}>
              <div style={{fontSize:32,marginBottom:8}}>📋</div>
              <p>{zoekterm ? 'Geen resultaten.' : 'Nog geen beoordelingen.'}</p>
            </div>
          ) : (
            gefilterdeSeSSies.map(sessie => {
              const urg    = URGENTIE_KLEUREN[sessie.urgentie] || { bg:'#F0F4FA', kleur:'#666', label: sessie.urgentie };
              const isOpen = openSessieId === sessie.id;
              return (
                <div key={sessie.id} className={styles.sessieWrap}>
                  <div className={`${styles.sessieHeader} ${isOpen ? styles.sessieOpen : ''}`}
                    onClick={() => { setOpenSessieId(isOpen ? null : sessie.id); setToonExtraKeuze(false); }}>
                    <div className={styles.sessieLinks}>
                      <span className={styles.sessieUrgentie} style={{ background: urg.bg, color: urg.kleur }}>
                        {urg.label}
                      </span>
                      <span className={styles.sessieOmschrijving}>
                        {sessie.omschrijving || 'Geen omschrijving'}
                      </span>
                    </div>
                    <div className={styles.sessieRechts}>
                      <span className={styles.sessieDatum}>
                        {new Date(sessie.aangemaakt_op).toLocaleDateString('nl-NL')}
                      </span>
                      <span className={styles.sessieAantal}>
                        {sessie.outputs?.length || 0}x
                      </span>
                      <span className={styles.sessieChevron}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className={styles.sessieBody}>
                      {sessie.gebruiker_naam && (
                        <div className={styles.sessieGebruiker}>👤 {sessie.gebruiker_naam}</div>
                      )}
                      {(sessie.outputs || []).map(output => (
                        <button key={output.id}
                          className={`${styles.outputRij} ${geselecteerde?.id === output.id ? styles.outputActief : ''}`}
                          onClick={() => openOutput(output)}>
                          <span className={styles.outputTypeBadge} style={{
                            background: OUTPUT_KLEUREN[output.output_type]?.bg || '#F0F4FA',
                            color:      OUTPUT_KLEUREN[output.output_type]?.kleur || '#1A1A2E',
                          }}>
                            {OUTPUT_LABELS[output.output_type]}
                          </span>
                          <span className={styles.outputDatum}>
                            {new Date(output.aangemaakt_op).toLocaleTimeString('nl-NL', { hour:'2-digit', minute:'2-digit' })}
                          </span>
                        </button>
                      ))}

                      <div className={styles.sessieActies}>
                        {(heeftPermissie('outputs.sbar') || heeftPermissie('outputs.artsconsult') ||
                          heeftPermissie('outputs.rapportage') || heeftPermissie('outputs.dienst_overdracht') ||
                          heeftPermissie('outputs.familie') || heeftPermissie('outputs.overplaatsing')) && (
                          <button className={styles.knopExtra}
                            onClick={() => { setToonExtraKeuze(!toonExtraKeuze); setExtraType(''); }}>
                            + Nieuwe output uit deze invoer
                          </button>
                        )}
                        {heeftPermissie('outputs.verwijderen') && (
                          <button className={styles.knopVerwijder}
                            onClick={() => setVerwijderModal(sessie)}>
                            🗑 Verwijderen
                          </button>
                        )}
                      </div>

                      {toonExtraKeuze && (
                        <div className={styles.extraWrap}>
                          <p className={styles.extraTitel}>Kies het outputtype:</p>
                          <div className={styles.extraGrid}>
                            {EXTRA_TYPES
                              .filter(t => heeftPermissie(t.p) && heeftModule(t.c.startsWith('outputs.') ? t.c : `outputs.${t.c}`))
                              .filter(t => !(sessie.outputs||[]).find(o => o.output_type === t.c))
                              .map(t => (
                                <button key={t.c}
                                  className={`${styles.extraKnop} ${extraType === t.c ? styles.extraActief : ''}`}
                                  onClick={() => setExtraType(t.c)}>
                                  {t.l}
                                </button>
                              ))}
                          </div>
                          {extraType && (
                            <button className={styles.knopGenereer}
                              onClick={() => handleExtraOutput(sessie.id)}
                              disabled={extraLaden}>
                              {extraLaden ? 'Genereren...' : `${OUTPUT_LABELS[extraType]} genereren →`}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* ── Detail view ── */}
        <div className={styles.detail}>
          {detailLaden && <div className={styles.laadTekst}>Laden...</div>}
          {!detailLaden && !geselecteerde && (
            <div className={styles.detailLeeg}>
              <div style={{fontSize:32,marginBottom:8}}>📄</div>
              <p>Selecteer een output om te bekijken.</p>
            </div>
          )}
          {!detailLaden && geselecteerde && (() => {
            const sbarData = parseInhoud(geselecteerde.inhoud, geselecteerde.output_type);
            return (
              <div className={styles.detailInhoud}>
                <div className={styles.detailHeader}>
                  <h2 className={styles.detailTitel}
                    style={{ color: OUTPUT_KLEUREN[geselecteerde.output_type]?.kleur || '#2D2B6B' }}>
                    {OUTPUT_LABELS[geselecteerde.output_type]}
                  </h2>
                  <div className={styles.detailActies}>
                    <button className={styles.knopActie} onClick={async () => {
                      const LABELS = { S:'Situation', B:'Background', A:'Assessment', R:'Recommendation' };
                      let tekstPlain, tekstHtml;
                      try {
                        if (geselecteerde.output_type === 'sbar') {
                          const data = typeof geselecteerde.inhoud === 'object'
                            ? geselecteerde.inhoud
                            : JSON.parse(geselecteerde.inhoud.replace(/```json\n?|\n?```/g,'').trim());
                          tekstPlain = Object.entries(data).map(([k,v])=>`${k} — ${LABELS[k]||k}:\n${v}`).join('\n\n');
                          tekstHtml  = Object.entries(data).map(([k,v])=>`<h3 style="color:#2D2B6B">${k} — ${LABELS[k]||k}</h3><p>${v}</p>`).join('');
                        } else {
                          const str = typeof geselecteerde.inhoud === 'string' ? geselecteerde.inhoud : JSON.stringify(geselecteerde.inhoud, null, 2);
                          tekstPlain = str.replace(/```[\w]*\n?|\n?```/g,'').replace(/\*\*/g,'').trim();
                          tekstHtml  = str.replace(/```[\w]*\n?|\n?```/g,'').trim().split('\n').map(line => {
                            if (!line.trim()) return '<br>';
                            if (line.trim().match(/^\*\*.*\*\*$/)) return `<h3 style="color:#2D2B6B;margin:12px 0 4px">${line.trim().replace(/\*\*/g,'')}</h3>`;
                            if (line.trim().match(/^[-*]\s/)) return `<p style="margin:2px 0 2px 16px">• ${line.trim().replace(/^[-*]\s/,'').replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>')}</p>`;
                            if (line.trim().match(/^\d+\.\s/)) return `<p style="margin:2px 0 2px 16px">${line.trim()}</p>`;
                            return `<p style="margin:0 0 6px">${line.replace(/\*\*(.*?)\*\*/g,'<strong>$1</strong>')}</p>`;
                          }).join('');
                        }
                      } catch {
                        tekstPlain = typeof geselecteerde.inhoud === 'string' ? geselecteerde.inhoud : JSON.stringify(geselecteerde.inhoud, null, 2);
                        tekstHtml  = `<p>${tekstPlain}</p>`;
                      }
                      try {
                        await navigator.clipboard.write([new ClipboardItem({'text/plain':new Blob([tekstPlain],{type:'text/plain'}),'text/html':new Blob([tekstHtml],{type:'text/html'})})]);
                      } catch { navigator.clipboard.writeText(tekstPlain); }
                      toast.success('Gekopieerd.');
                    }}>📋 Kopiëren</button>
                    <button className={styles.knopActie} onClick={() => handlePdf(geselecteerde.id)}>
                      📄 PDF
                    </button>
                  </div>
                </div>
                {sbarData ? (
                  ['S','B','A','R'].map(letter => {
                    if (!sbarData[letter]) return null;
                    const cfg = SBAR_CONFIG[letter];
                    return (
                      <div key={letter} className={styles.sbarKaart}
                        style={{ borderLeftColor: cfg.rand, background: `${cfg.rand}11` }}>
                        <div className={styles.sbarBadge} style={{ background: cfg.rand }}>{letter}</div>
                        <div className={styles.sbarContent}>
                          <div className={styles.sbarLabel}>{cfg.label} — {cfg.sub}</div>
                          <p className={styles.sbarTekst}>{sbarData[letter]}</p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.mdInhoud}>
                    {renderMarkdown(geselecteerde.inhoud)}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Verwijder modal */}
      {verwijderModal && (
        <div className={styles.modalOverlay} onClick={() => setVerwijderModal(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitel}>Sessie verwijderen</h3>
            <p className={styles.modalTekst}>
              Weet u zeker dat u <strong>"{verwijderModal.omschrijving || 'Geen omschrijving'}"</strong> en
              alle {verwijderModal.outputs?.length || 0} bijbehorende outputs wilt verwijderen?
              Dit kan niet ongedaan worden gemaakt.
            </p>
            <div className={styles.modalKnoppen}>
              <button className={styles.knopSecundair} onClick={() => setVerwijderModal(null)}>Annuleren</button>
              <button className={styles.knopVerwijderModal}
                onClick={() => handleVerwijderSessie(verwijderModal.id)}>
                Ja, verwijderen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
